import React from "react";
import FirstPage from "@mui/icons-material/FirstPage";
import LastPage from "@mui/icons-material/LastPage";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import NavigateNext from "@mui/icons-material/NavigateNext";
import { cn } from "../../utils/cn";
import { getVisiblePaginationPages } from "./getVisiblePaginationPages";

export interface PaginationBarProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

const iconClass = "h-[18px] w-[18px] text-[#666666]";

export const PaginationBar: React.FC<PaginationBarProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  className,
}) => {
  if (totalPages < 1) {
    return null;
  }

  const safeCurrent = Math.min(Math.max(1, currentPage), totalPages);
  const visible = getVisiblePaginationPages(safeCurrent, totalPages);

  return (
    <nav
      className={cn("flex flex-row flex-wrap items-center justify-center", className)}
      aria-label="Pagination"
    >
      <button
        type="button"
        className="flex h-9 w-9 items-center justify-center rounded border border-solid border-[#B6B6B6] bg-[#F9F9F9] disabled:opacity-40"
        aria-label="First page"
        disabled={safeCurrent <= 1}
        onClick={() => onPageChange(1)}
      >
        <FirstPage className={iconClass} fontSize="inherit" />
      </button>
      <button
        type="button"
        className="mx-1 flex h-9 w-9 items-center justify-center rounded border border-solid border-[#B6B6B6] bg-[#F9F9F9] disabled:opacity-40"
        aria-label="Previous page"
        disabled={safeCurrent <= 1}
        onClick={() => onPageChange(Math.max(1, safeCurrent - 1))}
      >
        <NavigateBefore className={iconClass} fontSize="inherit" />
      </button>

      <div className="flex flex-row flex-wrap items-center justify-center gap-0">
        {visible.map((token, index) =>
          token === "ellipsis" ? (
            <span
              key={`e-${index}`}
              className="mx-1 min-w-[1.25rem] text-center text-sm font-medium text-[#666666]"
              aria-hidden
            >
              …
            </span>
          ) : (
            <button
              key={token}
              type="button"
              className={cn(
                "mx-3 min-h-9 min-w-9 rounded-full border border-solid px-2 text-sm font-semibold transition-colors",
                token === safeCurrent
                  ? "border-[#6EA5FF] bg-[#6EA5FF] text-white"
                  : "border-transparent bg-transparent text-[#666666] hover:border-[#B6B6B6]"
              )}
              aria-current={token === safeCurrent ? "page" : undefined}
              onClick={() => onPageChange(token)}
            >
              {token}
            </button>
          )
        )}
      </div>

      <button
        type="button"
        className="mx-1 flex h-9 w-9 items-center justify-center rounded border border-solid border-[#B6B6B6] bg-[#F9F9F9] disabled:opacity-40"
        aria-label="Next page"
        disabled={safeCurrent >= totalPages}
        onClick={() => onPageChange(Math.min(totalPages, safeCurrent + 1))}
      >
        <NavigateNext className={iconClass} fontSize="inherit" />
      </button>
      <button
        type="button"
        className="flex h-9 w-9 items-center justify-center rounded border border-solid border-[#B6B6B6] bg-[#F9F9F9] disabled:opacity-40"
        aria-label="Last page"
        disabled={safeCurrent >= totalPages}
        onClick={() => onPageChange(totalPages)}
      >
        <LastPage className={iconClass} fontSize="inherit" />
      </button>
    </nav>
  );
};
